import { Component, OnChanges, Input } from "@angular/core";
import { SeriesLineOptions } from "highcharts";

import { AssemblyConfig, APIResults } from "src/app/sharedinterfaces";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
declare const BrandVars;

@Component({
	selector: "app-trend-ci",
	templateUrl: "./trend-ci.component.html",
	styleUrls: ["./trend-ci.component.scss"]
})
export class TrendCiComponent implements OnChanges {
	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	component: AssemblyConfig.Component;

	public brand = BrandVars;

	public regimes = undefined;
	public maintenancehistory: APIResults.MaintenanceHistory[] = undefined;

	public showOptional = false;

	public useXAxisDate = BrandVars.fixedassets;
	public dataExceedsLimit = false;
	public unlimitedChart = false;

	public ciCriticals: CIWithOptions[] = [];
	public ciOptionals: CIWithOptions[] = [];

	constructor(private api: ApiService, private charting: ChartingService) {
	}

	async ngOnChanges() {
		this.showOptional = false;
		await this.RefreshChart();
	}

	public async RefreshChart() {
		if (this.asset && this.component) {
			// Fetch the regimes to use later in the regime CI chart, if needed
			const [regimes, maintresult] = await Promise.all([
				this.api.GetRegimes(this.asset.asset_id),
				this.api.GetMaintenanceHistory(this.asset.asset_id),
			]);
			this.regimes = regimes;
			this.maintenancehistory = maintresult;
			this.ciCriticals = this.component.ci.filter(item => item.plot === undefined);
			await Promise.all(this.ciCriticals.map(async ci => {
				await this.PopulateCiOptions(ci);
			}));
			if (this.showOptional) {
				await this.enableOptionals();
			}
		}
	}

	async enableOptionals() {
		if (this.asset && this.component) {
			this.showOptional = true;
			this.ciOptionals = this.component.optional_ci.filter(item => item.plot === undefined);
			await Promise.all(this.ciOptionals.map(async ci => {
				await this.PopulateCiOptions(ci);
			}));
		}
	}
	async PopulateCiOptions(ci: CIWithOptions) {
		const xaxisTitle = this.useXAxisDate ? "Date" : (BrandVars.flightTime + " (hrs)");
		ci.options = undefined;
		ci.errormessage = undefined;
		const siblings = [ci];
		// If we have a paired plot CI, add it
		const dependent = this.component.ci.find(item => item.plot === ci.index) || this.component.optional_ci.find(item => item.plot === ci.index);
		if (dependent) {
			siblings.push(dependent);
		}
		try {
			const results = await Promise.all(siblings.map(async sibling => {
				const ciresult = await this.api.GetComponentCi(this.asset.asset_id, this.component.id, sibling.index, 0, this.unlimitedChart ? 1000000 : 15000);
				if (this.useXAxisDate) {
					for (let i = 0; i < ciresult.ci.length; i++) {
						ciresult.ci[i][0] = ciresult.dates[i] * 1000;
					}
				}
				this.dataExceedsLimit = (ciresult.ci && ciresult.ci.length >= 15000);
				return {
					ci: sibling,
					dates: ciresult.dates,
					data: ciresult.ci,
				};
			}));
			const series: SeriesLineOptions[] = results.map(formattedci => {
				return {
					type: "line",
					name: formattedci.ci.name,
					data: formattedci.data,
				};
			});
			const dates = results.map(result => result.dates);
			let pointDetails;
			// If this is a graph of a CI called "Regime Index", add a special formatter that looks up the regime index
			if (ci.name === "Regime Index") {
				pointDetails = (point: Highcharts.Point) => {
					if (this.regimes && this.regimes[point.y]) {
						return "<br/>Regime: " + this.regimes[point.y].name;
					}
					return "";
				};
			}
			ci.options = this.charting.FlightTimeWithMaint(ci.groupname || ci.name, ci.detail, xaxisTitle, this.useXAxisDate, series, dates, pointDetails, ci.units, this.fleet ? this.fleet.time_offset_hours : 0, ci.warning, ci.alarm, this.maintenancehistory, this.component.lru);
		} catch (err) {
			console.log(err);
			ci.errormessage = "Couldn't load CI trend";
		}
	}
}

interface CIWithOptions extends AssemblyConfig.CI {
	options?: Highcharts.Options;
	errormessage?: string;
}
